import React, { useEffect, useRef, useState } from 'react';
import MembershipPauseBadge from './MembershipPauseBadge';
import ModalPauseDateForm, { PauseDateFormMode, PauseDateFormValue } from './ModalPauseDateForm';
import {
    useCancelPause,
    useChangePauseEnd,
    useMembershipPauseSupport,
    useOpenPause,
    useResumePause,
} from '@/features/membershipPause/hooks';
import {
    buildPauseIntentKey,
    getStablePauseRequestKey,
    PauseAction,
    StablePauseRequestKey,
} from '@/features/membershipPause/requestKey';
import { resolvePauseError } from '@/features/membershipPause/errors';
import {
    formatKstDate,
    getKstTodayKey,
    getOpenPause,
    isPassInUse,
    hasOtherOpenPause,
    isPauseFormStale,
    PauseFormSnapshot,
    previewResume,
} from '@/features/membershipPause/status';
import { MembershipPauseState, PauseOp, PausePassKind, PausePassRef, PauseWriteResponse } from '@/features/membershipPause/types';

type AlertFn = (title: string, content: string, isError: boolean, ...rest: any[]) => void;
type TwoButtonAlertFn = (
    title: string,
    content: string,
    isError: boolean,
    cancelText?: string,
    confirmText?: string,
    onCancel?: () => void,
    onConfirm?: () => void,
    isExitButton?: boolean,
) => void;

type Props = {
    kind: PausePassKind;
    passId: string;
    centerId?: string | null;
    membership: {
        status?: string;
        isDeleted?: boolean;
        isCanceled?: boolean;
        expirationDate?: string | null;
        pause?: MembershipPauseState | null;
        schedules?: any[];
    } | null;
    /** 읽기 전용(탈퇴 회원·종료된 수강권 등)이면 배지만 보여준다 */
    isReadOnly?: boolean;
    createAlert: AlertFn;
    createTwoButtonAlert: TwoButtonAlertFn;
    /** 상세 다시 불러오기 */
    onRefresh: () => void;
};

const OP_LABEL: Record<string, string> = {
    OPEN: '정지 등록',
    RESUME: '해제',
    CANCEL: '정지 취소',
    END_CHANGE: '해제일 변경',
    HANDOVER: '양도·인계 전 해제',
};

const ROLE_LABEL: Record<string, string> = {
    coach: '강사',
    admin: '관리자',
    superAdmin: '슈퍼관리자',
    system: '자동',
};

const describeOp = (op: PauseOp) => {
    const period = `${formatKstDate(op.startAt, 'YYYY.MM.DD')} ~ ${op.endAt ? formatKstDate(op.endAt, 'YYYY.MM.DD') : '해제일 없음'}`;
    const extension =
        typeof op.days === 'number' && op.days > 0
            ? ` · +${op.days}일${op.expiryAfter ? ` (만료일 ${formatKstDate(op.expiryAfter)})` : ''}`
            : '';
    return `${period}${extension}`;
};

const NOOP = () => undefined;

/**
 * 수강권 상세의 일시정지 구역 — 배지, 정지하기 / 해제 / 해제일 바꾸기 / 정지 취소, 최근 작업.
 *
 * - 정지 기능이 없는 서버(구서버·롤백 빌드)면 배지만 남기고 버튼을 숨긴다.
 * - 만료일 미리보기는 표시용이다. 끝난 뒤 안내하는 만료일은 **서버 응답 값**이다.
 * - 요청 키는 동작마다 의도 문자열에 묶인다(같은 의도 재시도 = 같은 키).
 */
const MembershipPauseSection = ({
    kind,
    passId,
    centerId,
    membership,
    isReadOnly = false,
    createAlert,
    createTwoButtonAlert,
    onRefresh,
}: Props) => {
    const { isSupported } = useMembershipPauseSupport(centerId);
    const openMutation = useOpenPause();
    const resumeMutation = useResumePause();
    const cancelMutation = useCancelPause();
    const changeEndMutation = useChangePauseEnd();
    const keyRef = useRef<StablePauseRequestKey | null>(null);
    /** 열린 폼과 그 폼을 연 순간의 대상(정지·revision). 제출은 이 대상으로만 보낸다(검증 7). */
    const [form, setForm] = useState<{ mode: PauseDateFormMode; target: PauseFormSnapshot } | null>(null);
    const [showHistory, setShowHistory] = useState(false);

    const pause = membership?.pause ?? null;
    const open = getOpenPause(pause);
    const inUse = isPassInUse(membership);
    const revision = Number(pause?.revision ?? 0);
    const ops = Array.isArray(pause?.recentOps) ? [...(pause!.recentOps as PauseOp[])].reverse() : [];
    const canAct = isSupported && !isReadOnly && inUse && !!passId && (kind === 'pt' || !!centerId);
    const isBusy = openMutation.isLoading || resumeMutation.isLoading || cancelMutation.isLoading || changeEndMutation.isLoading;

    // 다시 불러온 뒤 대상이 바뀌었으면 열린 폼을 무효로 만든다 — 옛 정지 기준 날짜를 새 정지에 보내지 않게.
    useEffect(() => {
        if (!form || !isPauseFormStale(form.target, pause)) return;
        setForm(null);
        keyRef.current = null;
        createAlert('정지 정보가 바뀌었어요', '다른 곳에서 정지 정보가 바뀌었어요.\n새 정지 내용을 확인하고 다시 열어 주세요.', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, pause?.openId, pause?.revision]);

    if (!membership) return null;
    // 보여줄 것이 없으면(정지 없음 + 쓸 수 없음 + 이력 없음) 구역 자체를 그리지 않는다.
    if (!canAct && !open && ops.length === 0) return null;

    const ref: PausePassRef =
        kind === 'pt' ? { kind: 'pt', membershipId: passId } : { kind: 'group', centerId: String(centerId || ''), memberTicketId: passId };

    const keyFor = (action: PauseAction, extra: { pauseId?: string; startDate?: string | null; endDate?: string | null; expectedRevision?: number }) => {
        const next = getStablePauseRequestKey(keyRef.current, buildPauseIntentKey({ action, ref, ...extra }));
        keyRef.current = next;
        return next.requestKey;
    };

    const handleError = (error: unknown) => {
        const resolved = resolvePauseError(error);
        if (resolved.dropKey) keyRef.current = null;
        if (resolved.refetch) {
            // 다시 불러오면 대상이 바뀔 수 있다 — 폼은 닫고 안내 하나만 띄운다(무효화 안내와 겹치지 않게).
            setForm(null);
            onRefresh();
        }
        createAlert(resolved.title, resolved.message, true);
    };

    const handleSuccess = (title: string, res: PauseWriteResponse) => {
        // 성공 표시가 없는 2xx(본문을 못 읽음 등)는 결과 불명 — 키를 지키고 다시 불러온다(검증 9).
        if (!res?.success) {
            onRefresh();
            createAlert('결과 확인 실패', '결과를 확인하지 못했어요.\n화면을 다시 불러왔어요. 같은 내용으로 다시 누르면 같은 요청으로 처리돼요.', true);
            return;
        }
        // 해제·취소했는데 응답에 다른 정지가 열려 있으면, 이미 끝난 정지의 기록이 재생된 것이다(검증 2).
        // 닫힘 종류를 가리지 않는다(END_CHANGE·HANDOVER 포함, 검증 14). 등록은 새 정지가 곧 이번 작업이라 해당 없음.
        if (res.operation && res.operation.kind !== 'OPEN' && hasOtherOpenPause(res.operation, res.pause)) {
            keyRef.current = null;
            setForm(null);
            onRefresh();
            createAlert('다른 정지가 있어요', '이 정지는 이미 끝났고, 지금은 다른 정지가 열려 있어요.\n다시 불러온 정지 내용을 확인해 주세요.', true);
            return;
        }
        keyRef.current = null;
        setForm(null);
        onRefresh();
        const op = res.operation;
        const days = typeof op?.days === 'number' ? op.days : 0;
        if (op && (op.kind === 'RESUME' || op.kind === 'HANDOVER') && days > 0) {
            createAlert(title, `정지한 ${days}일만큼 만료일이 늘었어요.${res.expiry ? `\n새 만료일: ${formatKstDate(res.expiry)}` : ''}`, false);
            return;
        }
        if (op && (op.kind === 'RESUME' || op.kind === 'CANCEL')) {
            createAlert(title, '정지가 끝났어요. 만료일은 그대로예요.', false);
            return;
        }
        createAlert(title, title === '정지 등록 완료' ? '정지를 등록했어요.' : '해제일을 바꿨어요.', false);
    };

    const submitOpen = (value: PauseDateFormValue) => {
        if (!form) return;
        const expectedRevision = form.target.revision;
        const run = () => {
            const requestKey = keyFor('open', { startDate: value.startDate, endDate: value.endDate, expectedRevision });
            openMutation.mutate(
                { ref, body: { startDate: value.startDate, endDate: value.endDate, expectedRevision, requestKey } },
                { onSuccess: (res) => handleSuccess('정지 등록 완료', res), onError: handleError },
            );
        };
        if (value.retroCount > 0) {
            createTwoButtonAlert(
                '지난 날짜부터 정지',
                `이 기간에 수업 ${value.retroCount}회가 있었습니다.\n그래도 이 날짜부터 정지할까요?`,
                false,
                '취소',
                '정지하기',
                NOOP,
                run,
            );
            return;
        }
        run();
    };

    const submitChangeEnd = (value: PauseDateFormValue) => {
        // 지금 열린 정지가 아니라 **폼을 연 정지**로 보낸다. 바뀌었으면 위 무효화가 폼을 이미 닫았다.
        const pauseId = form?.target.pauseId;
        if (!form || !pauseId) return;
        const expectedRevision = form.target.revision;
        const requestKey = keyFor('changeEnd', { pauseId, endDate: value.endDate, expectedRevision });
        changeEndMutation.mutate(
            { ref, pauseId, body: { endDate: value.endDate, expectedRevision, requestKey } },
            { onSuccess: (res) => handleSuccess('해제일 변경 완료', res), onError: handleError },
        );
    };

    const confirmResume = () => {
        if (!open) return;
        const preview = previewResume(pause, membership.expirationDate ?? null, getKstTodayKey());
        const body = !preview || preview.isCancel
            ? '아직 시작 전이라 정지가 취소되고 만료일은 그대로예요.'
            : `오늘 날짜로 해제해요. 만료일이 ${preview.days}일 늘어나요${
                  preview.expiryBefore && preview.expiryAfter
                      ? `\n(${formatKstDate(preview.expiryBefore)} → ${formatKstDate(preview.expiryAfter)}, 예상)`
                      : ''
              }.`;
        createTwoButtonAlert('정지 해제', body, false, '닫기', '해제하기', NOOP, () => {
            const requestKey = keyFor('resume', { pauseId: open.openId });
            resumeMutation.mutate(
                { ref, pauseId: open.openId, requestKey },
                { onSuccess: (res) => handleSuccess('정지 해제 완료', res), onError: handleError },
            );
        });
    };

    const confirmCancel = () => {
        if (!open) return;
        createTwoButtonAlert(
            '정지 취소',
            '연장 없이 정지를 없애요.\n잘못 등록한 정지일 때만 쓰세요. 쉰 기간을 만료일에 더하려면 「해제」를 누르세요.',
            true,
            '닫기',
            '정지 취소',
            NOOP,
            () => {
                const requestKey = keyFor('cancel', { pauseId: open.openId });
                cancelMutation.mutate(
                    { ref, pauseId: open.openId, requestKey },
                    { onSuccess: (res) => handleSuccess('정지 취소 완료', res), onError: handleError },
                );
            },
        );
    };

    const buttonBase = 'h-[34px] rounded-[8px] px-[14px] text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-50';

    return (
        <div className="mb-[24px] rounded-[16px] border border-solid border-[#E5E7EB] bg-white px-[24px] py-[18px]">
            <div className="flex flex-row flex-wrap items-center justify-between gap-[12px]">
                <div className="flex flex-row flex-wrap items-center gap-[10px]">
                    <span className="text-[16px] font-bold text-[#111827]">일시정지</span>
                    {open ? (
                        <MembershipPauseBadge pass={membership} />
                    ) : (
                        <span className="text-[13px] text-[#6B7280]">정지 중이 아니에요</span>
                    )}
                </div>
                {canAct && (
                    <div className="flex flex-row flex-wrap items-center gap-[8px]">
                        {!open ? (
                            <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => setForm({ mode: 'open', target: { pauseId: null, revision } })}
                                className={`${buttonBase} border border-[#CBD5E1] bg-white text-[#334155]`}
                            >
                                정지하기
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={confirmCancel}
                                    className={`${buttonBase} border border-[#FCA5A5] bg-white text-[#DC2626]`}
                                >
                                    정지 취소
                                </button>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => open && setForm({ mode: 'changeEnd', target: { pauseId: open.openId, revision } })}
                                    className={`${buttonBase} border border-[#CBD5E1] bg-white text-[#334155]`}
                                >
                                    해제일 바꾸기
                                </button>
                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={confirmResume}
                                    className={`${buttonBase} bg-[#3D73DD] text-white`}
                                >
                                    {resumeMutation.isLoading ? '해제하는 중' : '지금 해제'}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
            {open && (
                <div className="mt-[10px] text-[13px] leading-[20px] text-[#4B5563]">
                    {`시작 ${formatKstDate(open.startAt)} · ${open.endAt ? `해제 예정 ${formatKstDate(open.endAt)}` : '해제일 없음'}`}
                    {' · 회원은 정지 기간에 앱으로 수업을 신청·예약할 수 없어요.'}
                </div>
            )}
            {ops.length > 0 && (
                <div className="mt-[12px]">
                    <button
                        type="button"
                        onClick={() => setShowHistory((v) => !v)}
                        className="text-[13px] font-semibold text-[#3D73DD]"
                    >
                        {showHistory ? '최근 정지 기록 접기' : `최근 정지 기록 보기 (${ops.length})`}
                    </button>
                    {showHistory && (
                        <ul className="mt-[8px] flex flex-col gap-[6px]">
                            {ops.map((op, index) => (
                                <li
                                    key={`${op.op}-${op.at}-${index}`}
                                    className="flex flex-row flex-wrap items-center gap-[8px] rounded-[8px] bg-[#F9FAFB] px-[12px] py-[8px] text-[12px] text-[#374151]"
                                >
                                    <span className="font-semibold text-[#111827]">{OP_LABEL[op.kind] || op.kind}</span>
                                    <span>{describeOp(op)}</span>
                                    <span className="text-[#6B7280]">
                                        {ROLE_LABEL[op.byRole] || op.byRole} · {formatKstDate(op.at, 'YYYY.MM.DD HH:mm')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {form && canAct && (
                <ModalPauseDateForm
                    key={`${form.mode}:${form.target.pauseId ?? ''}:${form.target.revision}`}
                    mode={form.mode}
                    kind={kind}
                    open={open}
                    pause={pause}
                    expiry={membership.expirationDate ?? null}
                    schedules={membership.schedules}
                    isSubmitting={isBusy}
                    onSubmit={form.mode === 'open' ? submitOpen : submitChangeEnd}
                    onClose={() => {
                        keyRef.current = null;
                        setForm(null);
                    }}
                />
            )}
        </div>
    );
};

export default MembershipPauseSection;
